import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../api';
import { US_STATES } from '../constants';
import { BottomNav } from './Home';

function Profile() {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        first_name: '', last_name: '', email: '', phone: '',
        address_line1: '', address_city: '', address_state: '', address_zip: '',
        tshirt_size: 'L', password: ''
    });

    const [message, setMessage] = useState({ text: '', type: '' });
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState([]);
    const [earnedTshirts, setEarnedTshirts] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleDeleteAccount = async () => {
        setDeleting(true);
        try {
            await authAPI.deleteMe();
            logout();
            navigate('/login', { state: { message: 'Your account has been deleted successfully. A confirmation email has been sent.' } });
        } catch (err) {
            setMessage({
                text: err.response?.data?.error || 'Failed to delete account. Please try again.',
                type: 'error'
            });
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        const loadProfileData = async () => {
            try {
                const res = await authAPI.getMe();
                const profileData = res.data.user;
                
                // Update form
                setFormData({
                    first_name: profileData.first_name || '',
                    last_name: profileData.last_name || '',
                    email: profileData.email || '',
                    phone: profileData.phone || '',
                    address_line1: profileData.address_line1 || '',
                    address_city: profileData.address_city || '',
                    address_state: profileData.address_state || '',
                    address_zip: profileData.address_zip || '',
                    tshirt_size: profileData.tshirt_size || 'L',
                    password: ''
                });

                // Set summary and earned t-shirts
                const checkinSummary = res.data.checkin_summary || [];
                setSummary(checkinSummary);
                setEarnedTshirts(checkinSummary.filter(s => Number(s.checked_in) >= Number(s.total_stops) && Number(s.total_stops) > 0));
            } catch (err) {
                console.error('Failed to load profile data:', err);
            }
        };

        loadProfileData();
    }, []); // Run once on mount

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validate = () => {
        if (!formData.first_name.trim()) return 'First name is required';
        if (!formData.last_name.trim()) return 'Last name is required';
        if (!formData.email.trim()) return 'Email is required';

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';

        if (formData.password && formData.password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ text: '', type: '' });

        const validationError = validate();
        if (validationError) {
            setMessage({ text: validationError, type: 'error' });
            return;
        }

        setLoading(true);

        try {
            // Remove password from payload if it's empty
            const payload = { ...formData };
            if (!payload.password) {
                delete payload.password;
            }

            await authAPI.updateMe(payload);
            
            // Update local state so other screens see the changes immediately
            updateUser(payload);
            
            setMessage({ text: 'Profile updated successfully!', type: 'success' });

            // Scroll to top to see message
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setMessage({
                text: err.response?.data?.error || 'Failed to update profile',
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const [showEdit, setShowEdit] = useState(false);
    const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'March 2026';

    return (
        <div style={styles.screen}>
            {/* Header section matching Home style */}
            <div style={styles.header}>
                <div style={styles.headerInner}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{ width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg, #B87333 0%, #8d5d36 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: 'white', fontWeight: 'bold', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                            {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </div>
                        <div>
                            <div style={styles.brandSmall}>THE COPPER ROAD MEMBER</div>
                            <h1 style={styles.h1}>{user?.first_name} {user?.last_name}</h1>
                            <div style={{ fontSize: 12, color: '#8899aa', marginTop: 4 }}>{user?.email} • Member since {memberSince}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={styles.content}>
                {/* Progress Per Road Section */}
                <div style={{ marginBottom: 24 }}>
                    <div style={styles.sectionTitle}>YOUR JOURNEY PROGRESS</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {summary.map(road => {
                            const percent = road.total_stops > 0 ? (road.checked_in / road.total_stops) * 100 : 0;
                            return (
                                <div key={road.road_slug} style={{ ...styles.card, padding: '16px 20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                        <div style={{ fontWeight: 'bold', fontSize: 15, color: '#1a2234' }}>{road.road_name}</div>
                                        <div style={{ fontSize: 13, fontWeight: '700', color: '#b8753d' }}>{road.checked_in} / {road.total_stops}</div>
                                    </div>
                                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${percent}%`, background: 'linear-gradient(90deg, #b8753d 0%, #d4895a 100%)', borderRadius: 4 }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8, fontWeight: 500 }}>
                                        {percent === 100 ? '✅ Road Completed!' : `${Math.round(percent)}% Complete`}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* T-Shirt Status Section */}
                <div style={{ marginBottom: 24 }}>
                    <div style={styles.sectionTitle}>T-SHIRT STATUS</div>
                    <div style={styles.card}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                            <div style={{ fontSize: 32 }}>👕</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', fontSize: 15, color: '#1a2234' }}>Size on file: <span style={{ color: '#b8753d' }}>{formData.tshirt_size || 'Not set'}</span></div>
                                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.4 }}>
                                    {earnedTshirts.length > 0 
                                        ? `You've earned ${earnedTshirts.length} t-shirt(s)! ` 
                                        : "Complete any road to earn its official t-shirt."}
                                    {user?.tshirt_shipped_at 
                                        ? ` Latest shipment: ${new Date(user.tshirt_shipped_at).toLocaleDateString()}`
                                        : (earnedTshirts.length > 0 && user?.address_line1) 
                                            ? " Processing for shipment." 
                                            : earnedTshirts.length > 0 
                                                ? " Please add a shipping address below." 
                                                : ""}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit Profile Section */}
                {!showEdit ? (
                    <button onClick={() => setShowEdit(true)} style={styles.button}>
                        Edit Profile & Address
                    </button>
                ) : (
                    <div style={styles.card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ ...styles.sectionTitle, marginTop: 0, border: 'none', padding: 0 }}>EDIT PROFILE</div>
                            <button onClick={() => setShowEdit(false)} style={{ background: 'none', border: 'none', color: '#6b7280', fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>CANCEL</button>
                        </div>
                        {message.text && (
                            <div style={message.type === 'success' ? styles.successMsg : styles.errorMsg}>
                                {message.text}
                            </div>
                        )}

                        <form onSubmit={(e) => { handleSubmit(e).then(() => { if (message.type === 'success') setShowEdit(false); }); }} style={styles.form}>
                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>FIRST NAME</label>
                                    <input name="first_name" value={formData.first_name} onChange={handleChange} style={styles.input} required />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>LAST NAME</label>
                                    <input name="last_name" value={formData.last_name} onChange={handleChange} style={styles.input} required />
                                </div>
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>EMAIL</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} style={styles.input} required />
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>NEW PASSWORD</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    style={styles.input}
                                    placeholder="Leave blank to keep current"
                                    minLength="8"
                                />
                            </div>

                            <div style={styles.sectionTitle}>Shipping Info</div>

                            <div style={styles.group}>
                                <label style={styles.label}>PHONE</label>
                                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} style={styles.input} />
                            </div>

                            <div style={styles.group}>
                                <label style={styles.label}>STREET ADDRESS</label>
                                <input name="address_line1" value={formData.address_line1} onChange={handleChange} style={styles.input} />
                            </div>

                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>CITY</label>
                                    <input name="address_city" value={formData.address_city} onChange={handleChange} style={styles.input} />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>STATE</label>
                                    <select
                                        name="address_state"
                                        value={formData.address_state}
                                        onChange={handleChange}
                                        style={styles.input}
                                    >
                                        <option value="">Select State</option>
                                        {US_STATES.map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={styles.row}>
                                <div style={styles.group}>
                                    <label style={styles.label}>ZIP CODE</label>
                                    <input name="address_zip" value={formData.address_zip} onChange={handleChange} style={styles.input} />
                                </div>
                                <div style={styles.group}>
                                    <label style={styles.label}>T-SHIRT SIZE</label>
                                    <select name="tshirt_size" value={formData.tshirt_size} onChange={handleChange} style={styles.input}>
                                        {['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </div>
                )}

                <button type="button" onClick={handleLogout} style={styles.logoutBtn}>
                    Sign Out
                </button>

                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#e11d48',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}
                    >
                        Delete Account
                    </button>
                </div>
            </div>

            {/* Account Deletion Modal */}
            {showDeleteModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: 20
                }}>
                    <div style={{
                        background: '#fff', borderRadius: 20, padding: 24,
                        maxWidth: 420, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ fontSize: 20, fontWeight: 'bold', color: '#111827', marginBottom: 12 }}>
                            Delete Account?
                        </div>
                        <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.5, marginBottom: 20 }}>
                            Are you sure you want to permanently delete your Copper Road account? 
                            This action cannot be undone. All your check-in history, earned t-shirts, 
                            and personal profile data will be permanently removed. A confirmation email 
                            will be sent to <strong>{formData.email}</strong>.
                        </p>
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                                style={{
                                    padding: '12px 20px', borderRadius: 30, border: '1px solid #d1d5db',
                                    background: '#f9fafb', color: '#374151', fontWeight: 'bold',
                                    fontSize: 14, cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteAccount}
                                disabled={deleting}
                                style={{
                                    padding: '12px 20px', borderRadius: 30, border: 'none',
                                    background: '#e11d48', color: '#fff', fontWeight: 'bold',
                                    fontSize: 14, cursor: 'pointer'
                                }}
                            >
                                {deleting ? 'Deleting...' : 'Delete Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <BottomNav active="" />
        </div>
    );
}

const styles = {
    screen: { background: '#f0ebe3', minHeight: '100vh', paddingBottom: 100, fontFamily: 'Inter, system-ui, sans-serif' },
    header: { background: '#1a2234', padding: '56px 20px 28px' },
    headerInner: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    brandSmall: { color: '#f59e0b', fontSize: 11, fontWeight: '700', letterSpacing: 1.5 },
    h1: { fontSize: 30, fontFamily: 'Georgia, serif', color: '#fff', margin: '6px 0 0', fontWeight: 'bold', lineHeight: 1.2 },

    content: { padding: '20px' },
    card: {
        background: '#fff', borderRadius: 18, padding: '24px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.05)'
    },
    sectionTitle: { fontSize: 13, fontWeight: 'bold', color: '#b8753d', letterSpacing: 1, marginTop: 24, marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 8 },

    errorMsg: {
        backgroundColor: '#fff1f2', color: '#e11d48', padding: '12px',
        borderRadius: '8px', fontSize: '13px', marginBottom: '20px', fontWeight: '500'
    },
    successMsg: {
        backgroundColor: '#f0fdf4', color: '#16a34a', padding: '12px',
        borderRadius: '8px', fontSize: '13px', marginBottom: '20px', fontWeight: '500'
    },

    form: { display: 'flex', flexDirection: 'column', gap: 16 },
    row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    group: { display: 'flex', flexDirection: 'column', gap: 8 },
    label: { fontSize: '10px', fontWeight: 'bold', color: '#a1a1aa', letterSpacing: 1 },
    input: {
        width: '100%', padding: '14px 16px', border: '1.2px solid #e4e4e7',
        borderRadius: '14px', fontSize: '15px', color: '#1c1c1c', outline: 'none',
        boxSizing: 'border-box', background: '#fff'
    },
    button: {
        width: '100%', padding: '16px', marginTop: 16,
        background: 'linear-gradient(135deg, #b8753d 0%, #8d5d36 100%)',
        color: 'white', border: 'none', borderRadius: '40px',
        fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
        boxShadow: '0 8px 20px rgba(184,117,61,0.4)'
    },
    logoutBtn: {
        width: '100%', padding: '16px', marginTop: 16,
        background: '#fff', color: '#e11d48',
        border: '1.5px solid #ffe4e6', borderRadius: '40px',
        fontWeight: 'bold', fontSize: '15px', cursor: 'pointer'
    }
};

export default Profile;
